import { PageHeader } from "@ant-design/pro-components";
import { SwapOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Empty, Form, Input, Modal, Space, Table, Tag, message } from "antd";
import { toJalaali } from "jalaali-js";
import { memo, useRef, useState } from "react";
import api from "../../../api/api";
import ConvertNumbersToEn from "../../../Util/ConvertNumbersToEn";
import styles from "./SwitchUsername.module.css";

const ERROR_MESSAGES = {
  INVALID_PHONE_OLD: "موبایل قبلی یا کد کشور آن نامعتبر است",
  INVALID_PHONE_NEW: "موبایل جدید یا کد کشور آن نامعتبر است",
  USER_NOT_FOUND_OLD: "کاربری با موبایل قبلی پیدا نشد",
  USER_NOT_FOUND_NEW: "کاربری با موبایل جدید پیدا نشد",
  SAME_USER: "هر دو شماره متعلق به یک کاربر هستند",
  STALE_PREVIEW: "اطلاعات تغییر کرده است. دوباره بارگذاری کنید",
};

const ORDER_STATUS = {
  success: { label: "موفق", color: "green" },
  init: { label: "در انتظار", color: "gold" },
  cancel: { label: "لغو", color: "red" },
};

const resolveError = (error) => {
  const code = error?.response?.data?.error?.message;
  return ERROR_MESSAGES[code] || code || "خطای ناشناخته";
};

const formatJalali = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "-";
  const j = toJalaali(date.getFullYear(), date.getMonth() + 1, date.getDate());
  return `${j.jy}-${String(j.jm).padStart(2, "0")}-${String(j.jd).padStart(2, "0")}`;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return amount.toLocaleString();
};

const displayName = (user) => {
  if (user?.pendingCreate) return "کاربر جدید";
  return `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || "بدون نام";
};

const orderColumns = [
  { title: "محصول", dataIndex: "productTitle", render: (value) => value || "-" },
  {
    title: "وضعیت",
    dataIndex: "status",
    render: (value) => {
      const status = ORDER_STATUS[value];
      return status ? <Tag color={status.color}>{status.label}</Tag> : value || "-";
    },
  },
  {
    title: "مبلغ",
    dataIndex: "amount",
    render: (value) => <span dir="ltr">{formatAmount(value)}</span>,
  },
  {
    title: "تاریخ پرداخت",
    dataIndex: "paymentAt",
    render: (value) => <span dir="ltr">{formatJalali(value)}</span>,
  },
];

const UserCard = ({ title, user }) => (
  <Card size="small" title={title}>
    <div className={styles.Name}>
      <strong>{displayName(user)}</strong>
    </div>
    <div className={styles.Meta} dir="ltr">
      +{user.country_code || "98"} {user.username}
    </div>
    {user.email ? <div className={styles.Meta}>{user.email}</div> : null}
    {user.pendingCreate ? (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: "0.75rem" }}
        message="این شماره کاربر ندارد. با تایید، کاربر ساخته می‌شود."
      />
    ) : null}
    {user.orders?.length ? (
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        columns={orderColumns}
        dataSource={user.orders}
      />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="سفارشی ثبت نشده" />
    )}
  </Card>
);

const SwitchUsername = () => {
  const [oldCountry, setOldCountry] = useState("98");
  const [oldMobile, setOldMobile] = useState("");
  const [newCountry, setNewCountry] = useState("98");
  const [newMobile, setNewMobile] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState({ message: "", type: "" });
  const requestId = useRef(0);

  const clearPreview = () => {
    requestId.current += 1;
    setPreview(null);
    setResult({ message: "", type: "" });
  };

  const payload = () => ({
    old: {
      countryCode: ConvertNumbersToEn((oldCountry || "").trim()) || "98",
      mobile: ConvertNumbersToEn((oldMobile || "").trim()),
    },
    new: {
      countryCode: ConvertNumbersToEn((newCountry || "").trim()) || "98",
      mobile: ConvertNumbersToEn((newMobile || "").trim()),
    },
  });

  const loadUsers = () => {
    const body = payload();
    if (!body.old.mobile || !body.new.mobile) {
      message.warning("هر دو شماره موبایل را وارد کنید");
      return;
    }

    const id = requestId.current + 1;
    requestId.current = id;
    setOldCountry(body.old.countryCode);
    setOldMobile(body.old.mobile);
    setNewCountry(body.new.countryCode);
    setNewMobile(body.new.mobile);
    setPreview(null);
    setResult({ message: "", type: "" });
    setLoading(true);

    api
      .post("/users/switch-mobile/preview", body)
      .then((res) => {
        if (requestId.current !== id) return;
        setPreview(res.data);
      })
      .catch((error) => {
        if (requestId.current !== id) return;
        setPreview(null);
        setResult({ message: resolveError(error), type: "error" });
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
  };

  const confirmSwitch = () => {
    if (!preview) return;
    const body = {
      ...payload(),
      oldUserId: preview.old.id,
      newUserId: preview.new.id,
    };

    Modal.confirm({
      title: "جابجایی شماره موبایل",
      content: preview.new.pendingCreate
        ? `کاربری با موبایل جدید وجود ندارد و ساخته می‌شود. شماره ${displayName(preview.old)} با این شماره جابجا شود؟`
        : `شماره ${displayName(preview.old)} و ${displayName(preview.new)} جابجا شود؟`,
      okText: "بله، جابجا شود",
      cancelText: "انصراف",
      onOk: () => {
        setSubmitting(true);
        return api
          .post("/users/switch-mobile", body)
          .then((res) => {
            const swapped = res.data;
            setPreview(null);
            setOldMobile("");
            setNewMobile("");
            setOldCountry("98");
            setNewCountry("98");
            setResult({
              type: "success",
              message: `جابجایی انجام شد. ${displayName(swapped.old)} اکنون +${swapped.old.country_code} ${swapped.old.username} است و ${displayName(swapped.new)} اکنون +${swapped.new.country_code} ${swapped.new.username} است.`,
            });
            message.success("شماره‌ها جابجا شد");
          })
          .catch((error) => {
            setResult({ message: resolveError(error), type: "error" });
          })
          .finally(() => setSubmitting(false));
      },
    });
  };

  return (
    <div className={styles.Page}>
      <PageHeader
        title="جابجایی شماره موبایل"
        avatar={{ icon: <SwapOutlined />, size: "large" }}
      />

      <Card className={styles.Card}>
        <Form layout="vertical" autoComplete="off">
          <Form.Item label="موبایل قبلی" required>
            <div className={styles.PhoneRow} dir="ltr">
              <Input
                className={styles.Country}
                value={oldCountry}
                addonBefore="+"
                inputMode="numeric"
                onChange={(e) => {
                  setOldCountry(e.target.value);
                  clearPreview();
                }}
              />
              <Input
                className={styles.Mobile}
                value={oldMobile}
                dir="ltr"
                inputMode="numeric"
                placeholder="9123456789"
                onChange={(e) => {
                  setOldMobile(e.target.value);
                  clearPreview();
                }}
                onPressEnter={loadUsers}
              />
            </div>
          </Form.Item>

          <Form.Item label="موبایل جدید" required>
            <div className={styles.PhoneRow} dir="ltr">
              <Input
                className={styles.Country}
                value={newCountry}
                addonBefore="+"
                inputMode="numeric"
                onChange={(e) => {
                  setNewCountry(e.target.value);
                  clearPreview();
                }}
              />
              <Input
                className={styles.Mobile}
                value={newMobile}
                dir="ltr"
                inputMode="numeric"
                placeholder="9123456789"
                onChange={(e) => {
                  setNewMobile(e.target.value);
                  clearPreview();
                }}
                onPressEnter={loadUsers}
              />
            </div>
          </Form.Item>

          <Space>
            <Button type="primary" loading={loading} onClick={loadUsers}>
              بارگذاری
            </Button>
            <Button
              danger
              disabled={!preview}
              loading={submitting}
              onClick={confirmSwitch}
            >
              تایید جابجایی
            </Button>
          </Space>
        </Form>

        {preview && (
          <div className={styles.Users}>
            <UserCard title="موبایل قبلی" user={preview.old} />
            <UserCard
              title={
                preview.new.pendingCreate
                  ? "موبایل جدید - یوزر در دیتابیس وجود نداشت"
                  : "موبایل جدید"
              }
              user={preview.new}
            />
          </div>
        )}

        {result.message ? (
          <Alert
            style={{ marginTop: "1rem" }}
            message={result.message}
            type={result.type}
          />
        ) : null}
      </Card>
    </div>
  );
};

export default memo(SwitchUsername);
